import type { AxiosError } from 'axios';
import { nextClientToastCode } from './toastCodes';

export type AppToastSeverity = 'success' | 'error' | 'info' | 'warning';

export type AppToastDiagnostics = {
  code: string;
  requestId?: string;
  jobId?: string;
  errorCode?: string;
};

export type AppToast = {
  severity: AppToastSeverity;
  message: string;
  diagnostics: AppToastDiagnostics;
};

export function inferToastSeverityFromMessage(message: string): AppToastSeverity {
  const lower = String(message || '').trim().toLowerCase();
  if (!lower) return 'info';
  if (lower.includes('failed') || lower.includes('error') || lower.includes('cannot') || lower.includes('could not')) {
    return 'error';
  }
  if (
    lower.includes('timeout') ||
    lower.includes('timed out') ||
    lower.includes('please refresh') ||
    lower.includes('no previous')
  ) {
    return 'warning';
  }
  if (
    lower.includes('success') ||
    lower.includes('saved') ||
    lower.includes('created') ||
    lower.includes('added') ||
    lower.includes('deleted') ||
    lower.includes('completed')
  ) {
    return 'success';
  }
  return 'info';
}

function pickString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function pickHeader(headers: unknown, key: string): string {
  if (!headers || typeof headers !== 'object') return '';
  const lowered = key.toLowerCase();
  const val =
    (headers as Record<string, unknown>)[key] ??
    (headers as Record<string, unknown>)[lowered] ??
    (headers as Record<string, unknown>)[key.toUpperCase()];
  return pickString(val);
}

export function createToast(
  severity: AppToastSeverity,
  message: string,
  diagnostics?: Partial<AppToastDiagnostics>
): AppToast {
  return {
    severity,
    message: pickString(message) || 'Operation finished.',
    diagnostics: {
      code: pickString(diagnostics?.code) || nextClientToastCode(),
      requestId: pickString(diagnostics?.requestId) || undefined,
      jobId: pickString(diagnostics?.jobId) || undefined,
      errorCode: pickString(diagnostics?.errorCode) || undefined,
    },
  };
}

/** Build the clipboard payload for toast/banner copy buttons (body + ids). */
export function buildToastCopyText(opts: {
  body?: string | null;
  code?: string | null;
  requestId?: string | null;
  jobId?: string | null;
  errorCode?: string | null;
}): string {
  const body = pickString(opts.body);
  const lines: string[] = [];
  if (body) lines.push(body);
  const code = pickString(opts.code);
  const requestId = pickString(opts.requestId);
  const jobId = pickString(opts.jobId);
  const errorCode = pickString(opts.errorCode);
  const meta: string[] = [];
  if (code) meta.push(`Frontend trace: ${code}`);
  if (requestId) meta.push(`Request id: ${requestId}`);
  if (jobId) meta.push(`Job id: ${jobId}`);
  if (errorCode) meta.push(`Error code: ${errorCode}`);
  if (meta.length) lines.push(meta.join('\n'));
  return lines.join('\n\n').trim();
}

export function toastFromApiError(
  error: unknown,
  fallbackMessage: string,
  diagnostics?: Partial<AppToastDiagnostics>
): AppToast {
  const err = error as AxiosError<{
    error?: string;
    message?: string;
    details?: string | Record<string, unknown>;
    code?: string;
    error_code?: string;
    request_id?: string;
    job?: { job_id?: string; error?: string; message?: string };
    job_id?: string;
  }>;
  const data = err?.response?.data;
  const detailsText =
    typeof data?.details === 'string' ? data.details.trim() : '';
  const main =
    pickString(data?.message) ||
    pickString(data?.error) ||
    pickString(err?.message) ||
    fallbackMessage;
  const message = detailsText ? `${main} Details: ${detailsText}` : main;
  const jobId =
    pickString(data?.job_id) ||
    pickString(data?.job?.job_id) ||
    pickHeader(err?.response?.headers, 'x-job-id');
  const requestId =
    pickString(data?.request_id) ||
    pickHeader(err?.response?.headers, 'x-request-id');
  const errorCode =
    pickString(data?.code) || pickString(data?.error_code) || undefined;
  return createToast('error', message, {
    ...diagnostics,
    requestId: diagnostics?.requestId || requestId,
    jobId: diagnostics?.jobId || jobId,
    errorCode: diagnostics?.errorCode || errorCode,
  });
}
